import { NavbarGroup } from '@/business-components/navbar-group/NavbarGroup';
import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { GlobalClassName } from '@/shared/constant/GlobalClassName';
import { NavigationTypeEnum, type Navigation } from '@/shared/constant/navigation/Navigation';
import { OnlineType } from '@/shared/constant/OnlineType';
import { Button, Drawer } from 'aui';
import { FoxIconDotsVertical } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import { BrandLogoArea } from '../../../brand-logo-area/BrandLogoArea';
import { NavRightLinkContent } from '../../../nav-right-link-content/NavRightLinkContent';
import type { useLevelNavbarGroup } from '../../hooks/useLevelNavbarGroup';
import styles from './FirstLevelNavbarContent.module.css';

export type FirstLevelNavbarContentProps = {
  firstLevelNavbarGroup: ReturnType<typeof useLevelNavbarGroup>['firstLevelNavbarGroup'];
  navigation: Navigation;
  className?: string;
};

export function FirstLevelNavbarContent(props: FirstLevelNavbarContentProps) {
  const { firstLevelNavbarGroup, navigation, className } = props;

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const displayThemeToggle = useDocsDataModelStore((x) => x.docsBaseConfig.displayThemeToggle);
  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);
  const onlineType = useDocsDataModelStore((x) => x.docsIdTypeData.onlineType);

  const hasNavbarGroup = React.useMemo(() => {
    return firstLevelNavbarGroup && navigation.type !== NavigationTypeEnum.None;
  }, [firstLevelNavbarGroup, navigation.type]);
  const hiddenDrawer = usePersistFn(() => {
    setOpenDrawer(false);
  });

  return (
    <div
      className={clsx(
        GlobalClassName.HeaderTopNavbar,
        'flex overflow-hidden py-2.5 os:justify-center',
        {
          ['os:hidden']: !hasNavbarGroup,
        },
        className,
      )}
    >
      <div
        className={clsx('flex h-9 w-0 flex-1 items-center px-5 os:px-8', {
          ['os:max-w-[1560px]']: !isOpenDebugPanel,
          ['os:max-w-[1800px]']: isOpenDebugPanel,
        })}
      >
        <BrandLogoArea className="max-os:w-0 max-os:flex-1" onVersionSelectClick={hiddenDrawer} />

        <div className="hidden w-0 flex-1 os:flex">
          {hasNavbarGroup &&
            firstLevelNavbarGroup &&
            navigation.type !== NavigationTypeEnum.None && (
              <>
                <NavbarGroup
                  {...firstLevelNavbarGroup}
                  className={clsx(GlobalClassName.HeaderTopNavbarLeft, 'w-0 flex-1 os:pl-8')}
                  onClick={hiddenDrawer}
                />

                <NavRightLinkContent
                  className="os:ml-8"
                  value={navigation.navRightLinkItems}
                  onClick={hiddenDrawer}
                  onVersionSelectClick={hiddenDrawer}
                />
              </>
            )}
        </div>

        <div className="flex justify-end gap-1 os:hidden">
          {onlineType === OnlineType.DocsSite && navigation.type !== NavigationTypeEnum.None && (
            <Button
              aria-label="open navigation drawer"
              type="text"
              icon={<FoxIconDotsVertical />}
              onClick={() => setOpenDrawer(true)}
            />
          )}
        </div>

        <Drawer
          className={clsx('os:hidden', styles.drawer)}
          placement="top"
          open={openDrawer}
          onClose={hiddenDrawer}
          title={<BrandLogoArea onVersionSelectClick={hiddenDrawer} />}
        >
          {firstLevelNavbarGroup && navigation.type !== NavigationTypeEnum.None && (
            <>
              <NavbarGroup
                {...firstLevelNavbarGroup}
                className={clsx(
                  GlobalClassName.HeaderTopNavbarLeft,
                  'flex-1 overflow-y-auto os:pl-8',
                )}
                onClick={hiddenDrawer}
              />

              {(!!navigation.navRightLinkItems.length || displayThemeToggle) && (
                <div className="flex-shrink-0">
                  <div className="mx-2 my-2.5 border-b border-color-split" />

                  <NavRightLinkContent
                    className="h-10"
                    value={navigation.navRightLinkItems}
                    onClick={hiddenDrawer}
                    onVersionSelectClick={hiddenDrawer}
                  />
                </div>
              )}
            </>
          )}
        </Drawer>
      </div>
    </div>
  );
}
